import styled from '@emotion/styled';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { FabricEditor } from 'modules/fabric-editor';
import {
  DEFAULT_PLAYER_RANKING,
  PlayerRankingStat,
  type PlayerRankingDirection,
  type PlayerRankingSettings,
} from 'modules/manager/domain/player-stats';
import { TemplateType } from 'modules/manager/domain/template-type';
import { selectActiveTemplate, setPlayerRankingLocal, updatePlayerRanking } from 'modules/manager/store';
import React, { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import CommitOptionalNumberField from '../shared/commit-optional-number-field';
import HelpBubble from '../shared/help-bubble';
import InspectorRow from '../shared/inspector-row';
import InspectorSection from '../shared/inspector-section';
import SelectField from '../shared/select-field';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';

interface CommonPlayerRankingSectionProps {
  editor: FabricEditor;
}

const STAT_OPTIONS = [
  { value: PlayerRankingStat.GOALS, label: msg`Голы` },
  { value: PlayerRankingStat.ASSISTS, label: msg`Голевые передачи` },
  { value: PlayerRankingStat.GOALS_ASSISTS, label: msg`Голы + передачи` },
  { value: PlayerRankingStat.GAMES, label: msg`Игры` },
  { value: PlayerRankingStat.YELLOW_CARDS, label: msg`Жёлтые карточки` },
  { value: PlayerRankingStat.RED_CARDS, label: msg`Красные карточки` },
];

const DIRECTION_OPTIONS = [
  { value: 'desc', label: msg`По убыванию` },
  { value: 'asc', label: msg`По возрастанию` },
];

const SECTION_HELP = (
  <span>
    <Trans>
      Определяет, кто из игроков чемпионата попадёт на какое место в шаблоне. Первое место («Игрок&nbsp;№&nbsp;1») получает игрок с
      лучшим значением выбранной статистики, второе — следующий и так далее.
      <br />
      <br />
      На то, <i>какие</i> данные показывают плейсхолдеры, это не влияет: шаблон может одновременно показывать и голы, и передачи —
      ранжирование лишь решает порядок игроков.
    </Trans>
  </span>
);

/**
 * Which statistic a player-statistics template ranks by. Template-level, like
 * «Раскладка» — the render link only names the championship and the template, so
 * this is the only place the ranking is decided.
 *
 * Replaces the create-time «Показатель» subtype: a template may show several
 * stats at once, so pinning the whole template to one indicator was the wrong
 * axis; only the ranking needs to name one.
 */
const CommonPlayerRankingSection: React.FC<CommonPlayerRankingSectionProps> = ({ editor }) => {
  const { t } = useLingui();
  const dispatch = useAppDispatch();
  const dispatchWithToast = useDispatchWithToast();
  const template = useAppSelector(selectActiveTemplate);
  const templateType = editor.placeholders?.getTemplateType();
  const ranking = template?.playerRanking ?? DEFAULT_PLAYER_RANKING;

  const commit = useCallback(
    (next: PlayerRankingSettings) => {
      if (!template) return;
      dispatch(setPlayerRankingLocal(next));
      // Раньше провал молчал: на экране — новое значение, на сервере — старое.
      dispatchWithToast(updatePlayerRanking({ id: template.id, playerRanking: next }), {
        error: msg`Не удалось сохранить ранжирование`,
      });
    },
    [dispatch, dispatchWithToast, template],
  );

  if (templateType !== TemplateType.TOP_PLAYERS_BY_INDICATORS || !template) return null;

  const statOptions = STAT_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }));
  const directionOptions = DIRECTION_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }));

  return (
    <InspectorSection title={t`Ранжирование`} headerAction={<HelpBubble content={SECTION_HELP} />}>
      <InspectorRow label={t`Статистика`}>
        <SelectField
          value={ranking.stat}
          onChange={(v) => commit({ ...ranking, stat: v as PlayerRankingStat })}
          options={statOptions}
        />
      </InspectorRow>
      <InspectorRow label={t`Порядок`}>
        <FieldRow>
          <SelectField
            value={ranking.direction}
            onChange={(v) => commit({ ...ranking, direction: v as PlayerRankingDirection })}
            options={directionOptions}
          />
          <HelpBubble
            content={
              <span>
                <Trans>
                  <b>По убыванию</b> — первым идёт игрок с наибольшим значением (лучший бомбардир).
                  <br />
                  <b>По возрастанию</b> — первым идёт игрок с наименьшим значением.
                </Trans>
              </span>
            }
          />
        </FieldRow>
      </InspectorRow>
      <InspectorRow label={t`Топ N`}>
        <FieldRow>
          <CommitOptionalNumberField
            value={ranking.limit}
            placeholder={t`все`}
            min={1}
            onCommit={(v) => commit(v === undefined ? omitLimit(ranking) : { ...ranking, limit: v })}
          />
          <HelpBubble
            content={
              <span>
                <Trans>
                  Сколько игроков рендерить. Пусто — все игроки чемпионата со статистикой.
                  <br />
                  <br />
                  Если задать меньше, чем мест нарисовано в шаблоне, лишние блоки не попадут в картинку — см. «Раскладка игроков».
                </Trans>
              </span>
            }
          />
        </FieldRow>
      </InspectorRow>
    </InspectorSection>
  );
};

export default CommonPlayerRankingSection;

// The field is genuinely optional: an absent `limit` means "no limit", which is
// not the same as any number, so clearing it must drop the key rather than store
// a sentinel.
function omitLimit(ranking: PlayerRankingSettings): PlayerRankingSettings {
  const { limit, ...rest } = ranking;
  return rest;
}

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;

  & > :first-child {
    flex: 1;
    min-width: 0;
  }

  & > :last-child {
    flex: 0 0 auto;
  }
`;
